// src/services/profiles/authApi.ts

import baseApi from "../../lib/baseApi";
import publicApi from "../../lib/publicApi";
import {
  CompleteProfileLanguageResponseType,
  ProfileLanguagesResponseType,
} from "../../types/profiles/authTypes";

// 🔐 Fetch latest authenticated user snapshot
export const fetchAuthMeApi = async () => {
  const response = await baseApi.get("/accounts/auth/me/");
  return response.data;
};

// Register API call with error handling
export const registerUserApi = async (
  email: string,
  password: string,
  agreeToTerms: boolean,
  invite_code?: string
) => {
  const response = await publicApi.post("/accounts/auth/register/", {
    email,
    password,
    agree_to_terms: agreeToTerms,
    invite_code,
  });

  return response.data;
};

// Verify API call
export const verifyUser = async (activeCode: string) => {
  const response = await publicApi.post("/accounts/auth/verify/", {
    active_code: activeCode,
  });

  return response.data;
};

// Choose Path API call 
export const choosePathApi = async (category: string) => {
  const response = await publicApi.post(
    "/accounts/auth/choose-path/",
    {
      category,
    },
    {
      withCredentials: true,
    }
  );

  return response.data;
};

// Fetch profile language options -----------------------------------------------
export const fetchProfileLanguagesApi =
  async (): Promise<ProfileLanguagesResponseType> => {
    const response = await baseApi.get("/accounts/auth/profile-languages/");
    return response.data;
  };

// Complete profile language onboarding -----------------------------------------
export const completeProfileLanguageApi = async (
  primaryLanguage: string,
): Promise<CompleteProfileLanguageResponseType> => {
  const response = await baseApi.patch("/accounts/auth/profile-languages/", {
    primary_language: primaryLanguage,
    secondary_language: null,
  });

  return response.data;
};

// Login API call --------------------------------------------------------------------
// identifier can be email OR username.
export const loginUser = async (identifier: string, password: string) => {
  const response = await publicApi.post("/accounts/auth/login/", {
    identifier,
    password,
  });

  return response.data;
};

// API call for login with 2FA -------------------------------------------------------
// identifier can be canonical email or username.
// Backend supports both, but Login step should store the canonical email when returned.
export const loginWith2FA = async (
  otpCode: string,
  identifier: string
) => {
  try {
    const response = await publicApi.post("/accounts/auth/login-with-2fa/", {
      otp_code: otpCode,
      identifier,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

// Logout API call -------------------------------------------------------------------
export const logoutUser = async (refreshToken: string, deviceId?: string) => {
  try {
    const response = await baseApi.post("/accounts/auth/logout/", {
      refresh: refreshToken,
      device_id: deviceId || null,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

// Verify Password API ---------------------------------------------------------------
export const verifyPasswordApi = async (password: string) => {
  try {
    const response = await baseApi.post("/accounts/auth/verify-password/", {
      password,
    });

    return response.data;
  } catch (error: any) {
    throw error.response?.data || {
      valid: false,
      error: "Verification failed",
    };
  }
};

// Forget Password API call ----------------------------------------------------------
export const forgetPassword = async (email: string) => {
  try {
    const response = await publicApi.post("/accounts/auth/forget-password/", {
      email,
    });

    return response.data;
  } catch (err: any) {
    throw err;
  }
};

// Reset Password API call -----------------------------------------------------------
export const resetPassword = async (
  resetToken: string,
  newPassword: string
) => {
  const response = await publicApi.post(
    `/accounts/auth/reset-password/${resetToken}/`,
    {
      new_password: newPassword,
    }
  );

  return response.data;
};

// Change Password API call ----------------------------------------------------------
export const changePassword = async (
  oldPassword: string,
  newPassword: string
) => {
  try {
    const response = await baseApi.post("/accounts/auth/change-password/", {
      old_password: oldPassword,
      new_password: newPassword,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

// API call to enable PIN security ---------------------------------------------------
export const enablePinApi = async (
  accessPin: string,
  deletePin: string
) => {
  try {
    const response = await baseApi.post("/accounts/auth/enable-pin/", {
      access_pin: accessPin,
      delete_pin: deletePin,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

// API call to disable PIN security --------------------------------------------------
export const disablePinApi = async (pin: string) => {
  try {
    const response = await baseApi.post("/accounts/auth/disable-pin/", {
      pin,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

// API call to enable 2FA ------------------------------------------------------------
export const enable2FA = async () => {
  try {
    const response = await baseApi.post("/accounts/auth/enable-2fa/");
    return response.data;
  } catch (error) {
    throw error;
  }
};

// API call to verify 2FA token ------------------------------------------------------
export const verify2FAToken = async (otpCode: string) => {
  try {
    const response = await baseApi.post("/accounts/auth/verify-2fa-token/", {
      otp_code: otpCode,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

// API call to disable 2FA -----------------------------------------------------------
export const disable2FA = async () => {
  try {
    const response = await baseApi.post("/accounts/auth/disable-2fa/");
    return response.data;
  } catch (error) {
    throw error;
  }
};

// API call to verify 2FA disable token ----------------------------------------------
export const verifyDisable2FAToken = async (otpCode: string) => {
  try {
    const response = await baseApi.post(
      "/accounts/auth/verify-disable-2fa-token/",
      {
        otp_code: otpCode,
      }
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

// Account deletion APIs --------------------------------------------------------
// Backend endpoint names are retained for backward compatibility.
// "Reactivate" endpoints now cancel a pending permanent deletion request.
export const sendDeleteConfirmation = async () => {
  const response = await baseApi.post(
    "/accounts/auth/send-delete-confirmation/"
  );

  return response.data;
};

export const confirmDeleteAccount = async (code: string) => {
  const response = await baseApi.post(
    "/accounts/auth/confirm-delete-account/",
    {
      code,
    }
  );

  return response.data;
};

export const sendReactivateConfirmation = async () => {
  const response = await baseApi.post(
    "/accounts/auth/send-reactivate-confirmation/"
  );

  return response.data;
};

export const confirmReactivateAccount = async (code: string) => {
  const response = await baseApi.post(
    "/accounts/auth/confirm-reactivate-account/",
    {
      code,
    }
  );

  return response.data;
};